<script setup>
const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  offer: {
    type: String,
    required: true,
  },
  buttonText: {
    type: String,
    default: 'Join now',
  },
  image: {
    type: String,
    required: true,
  },
  imageAlt: {
    type: String,
    default: '',
  },
})

const asset = (path) => `${import.meta.env.BASE_URL}${path}`
</script>

<template>
  <section class="promo-banner" aria-label="Promotion">
    <div class="promo-banner__inner">
      <div class="promo-banner__image">
        <img :src="asset(props.image)" :alt="props.imageAlt" />
      </div>

      <div v-if="props.title || props.offer || props.buttonText" class="promo-banner__content">
        <p v-if="props.title" class="promo-banner__title">{{ props.title }}</p>
        <h1 v-if="props.offer" class="promo-banner__offer">{{ props.offer }}</h1>
        <button v-if="props.buttonText" class="promo-banner__button" type="button">{{ props.buttonText }}</button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.promo-banner {
  width: 100%;
  background: var(--color-page-bg);
}

.promo-banner__inner {
  position: relative;
  width: min(100%, 1280px);
  margin: 0 auto;
  overflow: hidden;
}

.promo-banner__image {
  position: relative;
  width: 100%;
  display: flex;
  align-items: end;
  aspect-ratio: 802 / 400;
}

/* КАРТИНКА */
.promo-banner__image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* ОВЕРЛЕЙ СЛЕВА И СПРАВА */
.promo-banner__image::before,
.promo-banner__image::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  width: clamp(120px, 14vw, 260px);
  pointer-events: none;
  z-index: 2;
}

.promo-banner__image::before {
  left: 0;
  background: linear-gradient(
    to right,
    var(--color-page-bg),
    transparent
  );
}

.promo-banner__image::after {
  right: 0;
  background: linear-gradient(
    to left,
    var(--color-page-bg),
    transparent
  );
}

/* КОНТЕНТ */
.promo-banner__content {
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 22px 20px 30px;
  text-align: center;
}

.promo-banner__title {
  margin: 0 0 12px;
  color: var(--color-accent);
  font-size: clamp(1rem, 5vw, 1.5rem);
  font-weight: 500;
  line-height: 1.1;
}

.promo-banner__offer {
  max-width: 560px;
  margin: 0 0 24px;
  color: #fff;
  font-size: clamp(1.5rem, 7vw, 3rem);
  font-weight: 800;
  line-height: 1.16;
  text-transform: uppercase;
}

.promo-banner__button {
  min-width: min(100%, 300px);
  min-height: 50px;
  padding: 0 24px;
  border: 0;
  border-radius: 5px;
  color: #fff;
  background: var(--color-accent);
  font-size: clamp(0.95rem, 4vw, 1.25rem);
  font-weight: 700;
  text-transform: uppercase;
  transition: background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.promo-banner__button:hover {
  background-color: var(--color-accent-hover);
  box-shadow: var(--shadow-accent-hover);
}

/* TABLET+ */
@media (min-width: 768px) {
  .promo-banner__inner {
    height: 400px;
  }

  .promo-banner__image {
    height: 100%;
    aspect-ratio: auto;
  }

  .promo-banner__image img {
    object-position: center right;
  }

  .promo-banner__content {
    position: absolute;
    inset: 0 auto 0 0;
    align-items: center;
    justify-content: center;
    width: min(46%, 560px);
    padding: 40px 42px;
    background: transparent;
  }

  .promo-banner__title {
    margin-bottom: 14px;
    font-size: clamp(1.75rem, 2.4vw, 2.25rem);
  }

  .promo-banner__offer {
    margin-bottom: 28px;
    font-size: clamp(3rem, 4.2vw, 3.75rem);
    line-height: 1.14;
  }

  .promo-banner__button {
    min-width: 292px;
    min-height: 50px;
    font-size: 1.2rem;
  }
}

/* DESKTOP */
@media (min-width: 1200px) {
  .promo-banner__content {
    padding: 48px 56px;
  }
}
</style>
